import React, { useState, useEffect, useCallback } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Avatar,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import authService from '../services/authService';

function Login() {
  const { t } = useTranslation();
  usePageTitle(t('auth.signIn'));
  const navigate = useNavigate();

  // Tab: 0 = password, 1 = Smart-ID, 2 = Mobile-ID
  const [tab, setTab] = useState(0);

  // Password login
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // eID login
  const [idCode, setIdCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState(null);
  const [eidMethod, setEidMethod] = useState(null);
  const [polling, setPolling] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.login(username, password);
      navigate('/dashboard');
    } catch (err) {
      setError(
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.detail ||
        t('auth.loginFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEidStart = async (method) => {
    if (!idCode.trim()) {
      setError(t('auth.idCodeRequired'));
      return;
    }
    if (method === 'mobileid' && !phoneNumber.trim()) {
      setError(t('auth.phoneRequired'));
      return;
    }
    setError('');
    setLoading(true);
    setEidMethod(method);
    try {
      const data = { id_code: idCode.trim(), country: 'EE' };
      if (method === 'mobileid') data.phone_number = phoneNumber.trim();
      const res = await authService.eidStart(method, data);

      if (res.status === 202) {
        setVerificationCode(res.data.verification_code);
        setPolling(true);
      } else {
        // Immediate success (unlikely)
        authService.eidLogin(res.data);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || t('auth.eidFailed'));
      setEidMethod(null);
    } finally {
      setLoading(false);
    }
  };

  const pollEid = useCallback(async () => {
    if (!polling || !eidMethod) return;
    try {
      const res = await authService.eidPoll(eidMethod);
      if (res.status === 200) {
        setPolling(false);
        authService.eidLogin(res.data);
        navigate('/dashboard');
      }
      // 202 = still pending, keep polling
    } catch (err) {
      setPolling(false);
      setVerificationCode(null);
      setEidMethod(null);
      setError(err.response?.data?.error || t('auth.eidFailed'));
    }
  }, [polling, eidMethod, navigate, t]);

  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(pollEid, 2000);
    return () => clearInterval(interval);
  }, [polling, pollEid]);

  const cancelEid = () => {
    setPolling(false);
    setVerificationCode(null);
    setEidMethod(null);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              {t('auth.signIn')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Verification code display during eID polling */}
          {verificationCode && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('auth.verificationCode')}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: 8, mb: 2 }}>
                {verificationCode}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('auth.confirmOnDevice')}
              </Typography>
              <CircularProgress size={24} sx={{ mb: 2 }} />
              <br />
              <Button variant="text" onClick={cancelEid} size="small">
                {t('common.cancel')}
              </Button>
            </Box>
          )}

          {!verificationCode && (
            <>
              <Tabs
                value={tab}
                onChange={(e, v) => { setTab(v); setError(''); }}
                variant="fullWidth"
                sx={{ mt: 2, mb: 1 }}
              >
                <Tab label={t('auth.password')} />
                <Tab label="Smart-ID" icon={<FingerprintIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
                <Tab label="Mobile-ID" icon={<SmartphoneIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
              </Tabs>

              {/* Password login */}
              {tab === 0 && (
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                  <TextField
                    margin="normal" required fullWidth
                    label={t('auth.usernameOrEmail')}
                    autoComplete="username" autoFocus
                    value={username} onChange={(e) => setUsername(e.target.value)}
                  />
                  <TextField
                    margin="normal" required fullWidth
                    label={t('auth.password')} type="password"
                    autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                  />
                  <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 2 }} disabled={loading}>
                    {loading ? t('auth.signingIn') : t('auth.signIn')}
                  </Button>
                </Box>
              )}

              {/* Smart-ID login */}
              {tab === 1 && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth label={t('auth.idCode')} value={idCode}
                    onChange={(e) => setIdCode(e.target.value)}
                    placeholder="e.g. 38001010000"
                    sx={{ mb: 2 }}
                  />
                  <Button
                    fullWidth variant="contained" onClick={() => handleEidStart('smartid')}
                    disabled={loading} startIcon={<FingerprintIcon />}
                    sx={{ mb: 2 }}
                  >
                    {loading ? <CircularProgress size={20} /> : t('auth.signInSmartId')}
                  </Button>
                </Box>
              )}

              {/* Mobile-ID login */}
              {tab === 2 && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth label={t('auth.idCode')} value={idCode}
                    onChange={(e) => setIdCode(e.target.value)}
                    placeholder="e.g. 38001010000"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth label={t('auth.phoneNumber')} value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+372 5123 4567"
                    sx={{ mb: 2 }}
                  />
                  <Button
                    fullWidth variant="contained" onClick={() => handleEidStart('mobileid')}
                    disabled={loading} startIcon={<SmartphoneIcon />}
                    sx={{ mb: 2 }}
                  >
                    {loading ? <CircularProgress size={20} /> : t('auth.signInMobileId')}
                  </Button>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />
              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/register" variant="body2">
                  {t('auth.noAccount')}
                </Link>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default Login;
